import React, { useEffect } from 'react';

import { TrashCan } from '../../icons/trash-can';
import { Button } from '../button';
import { FormStickyFooter } from '../form/form';
import { MenuItem } from '../menu-item';
import { Select } from '../select';

import { OperatorInput } from './form-elements/operator-input';
import {
  AttributeRow,
  CombinatorBadge,
  Condition,
  QueryActions,
  QueryBuilderContainer,
  RuleGroupContainer,
  StyledContentCard,
  StyledIconButton,
  StyledSelect,
  VerticalLine,
} from './query-builder.styles';
import useQueryBuilder from './use-query-builder';
import { getOperatorGroupFromRule } from './utils';

import type { QueryBuilderUnit } from './form-elements/operator-input';
import type { Operator } from './form-elements/operators';
import type { Rule, RuleGroup, UseQueryBuilderProps } from './use-query-builder';

type QueryBuilderProps = UseQueryBuilderProps & {
  disabled?: boolean;
  disableSaveButton?: boolean;
  isLoading?: boolean;
  isQueryBuilderDirty?: boolean;
  previewQuery?: (ruleGroup: RuleGroup) => void;
  saveQuery: (ruleGroup: RuleGroup) => void;
  setIsQueryBuilderDirty?: (isDirty: boolean) => void;
  titleCopy: string;
  titleCopyDisabled: string;
};

export function QueryBuilder(props: QueryBuilderProps) {
  const {
    disabled,
    schema,
    queryRules,
    titleCopy,
    titleCopyDisabled,
    disableSaveButton,
    isLoading,
    saveQuery,
    isQueryBuilderDirty,
    setIsQueryBuilderDirty,
    previewQuery,
  } = props;

  const { ruleGroup, addRule, removeRule, updateRule, updateCombinator, areQueryRulesValid } = useQueryBuilder({
    queryRules,
    schema,
    setIsQueryBuilderDirty,
  });

  const { rules, combinator } = ruleGroup;

  useEffect(() => {
    if (previewQuery && rules.length > 0 && areQueryRulesValid) {
      previewQuery(ruleGroup);
    }
  }, [previewQuery, ruleGroup, areQueryRulesValid, rules.length]);

  //   On change handlers
  const handleConditionChange = (id: string, value: string) => {
    updateRule(id, { Condition: value, Attribute: '', Operator: '', Value: '', Variant: '' });
  };

  const handleAttributeChange = (id: string, value: string) => {
    updateRule(id, { Attribute: value, Operator: '', Value: '', Variant: '' });
  };

  const handleOperatorChange = (id: string, operator: string) => {
    const previousRuleState = ruleGroup.rules.find((rule) => rule.Id === id);
    const ruleOperatorGroup = previousRuleState && getOperatorGroupFromRule({ rule: previousRuleState, schema });

    const previousRuleStateOperator = ruleOperatorGroup?.find(
      (operator: Operator) => operator.name === previousRuleState?.Operator
    );
    const newOperator = ruleOperatorGroup?.find((oper: Operator) => oper.name === operator);

    // clear out all values when switching to a new valueType operator
    if (previousRuleStateOperator?.valueType !== newOperator?.valueType) {
      updateRule(id, { Operator: operator, Value: '', SecondValue: '', Variant: '' });
      return;
    }

    // default is to update the operator and keep the previous values when switching to the same valueType operator
    updateRule(id, { Operator: operator });
  };

  const handleValueChange = (id: string, value: string, secondValue?: boolean) => {
    if (secondValue) {
      updateRule(id, { SecondValue: value });
    } else {
      updateRule(id, { Value: value });
    }
  };

  const handleVariantChange = (id: string, variant: string) => {
    updateRule(id, { Variant: variant });
  };

  //   Getters
  const getAttributeSchema = (rule: Rule) =>
    schema[rule.Condition].attributes.find((attribute) => attribute.name === rule.Attribute);

  const isSaveButtonDisabled =
    disableSaveButton || isLoading || !areQueryRulesValid || !isQueryBuilderDirty || rules.length === 0;

  return (
    <>
      <StyledContentCard title={disabled ? titleCopyDisabled : titleCopy}>
        <QueryBuilderContainer>
          <RuleGroupContainer>
            {rules.map((rule, index) => (
              <React.Fragment key={rule.Id}>
                {/* Select a condition */}
                <Condition>
                  <Select
                    automationId={`query-builder_select-condition_select-${String(index)}`}
                    disabled={disabled}
                    placeholder='Select a condition'
                    value={rule.Condition}
                    onChange={(e) => handleConditionChange(String(rule.Id), e.target.value)}
                  >
                    {Object.values(schema).map(({ label, value }) => (
                      <MenuItem
                        automationId={`select-condition_${value.toLowerCase()}_option-${String(index)}`}
                        key={value}
                        value={value}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </Condition>

                {/* Select an attribute */}
                {rule.Condition && (
                  <AttributeRow>
                    <StyledSelect
                      automationId={`query-builder_select-attribute_select-${String(index)}`}
                      disabled={disabled}
                      placeholder='Select an attribute'
                      value={rule.Attribute}
                      onChange={({ target }) => handleAttributeChange(String(rule.Id), target.value)}
                    >
                      {schema[rule.Condition].attributes.map(({ label, name }) => (
                        <MenuItem key={name} value={name}>
                          {label}
                        </MenuItem>
                      ))}
                    </StyledSelect>

                    {/* Select an operator */}
                    {rule.Attribute && (
                      <OperatorInput
                        disabled={disabled}
                        index={index}
                        operator={rule.Operator}
                        operators={getOperatorGroupFromRule({ rule, schema })}
                        secondValue={rule.SecondValue}
                        unit={getAttributeSchema(rule)?.unit as QueryBuilderUnit}
                        value={rule.Value ?? ''}
                        variant={rule.Variant}
                        variants={getAttributeSchema(rule)?.variants ?? []}
                        onOperatorChange={(operator) => handleOperatorChange(String(rule.Id), operator)}
                        onValueChange={(value, secondValue) => handleValueChange(String(rule.Id), value, secondValue)}
                        onVariantChange={(variant) => handleVariantChange(String(rule.Id), variant)}
                      />
                    )}

                    <VerticalLine />

                    {!disabled && (
                      <StyledIconButton
                        automationId={`query-builder_delete-rule_button-${String(index)}`}
                        onClick={() => removeRule(String(rule.Id))}
                      >
                        <TrashCan />
                      </StyledIconButton>
                    )}
                  </AttributeRow>
                )}
                {index < rules.length - 1 && <CombinatorBadge label={combinator?.toUpperCase()} />}
              </React.Fragment>
            ))}
          </RuleGroupContainer>

          {!disabled && (
            <QueryActions>
              {rules.length > 0 && (
                <Select
                  placeholder='AND/OR'
                  value={combinator}
                  onChange={({ target }) => updateCombinator(target.value)}
                >
                  <MenuItem value='and'>AND</MenuItem>
                  <MenuItem value='or'>OR</MenuItem>
                </Select>
              )}
              <Button label='Add rule' variant='secondary' onClick={addRule} />
            </QueryActions>
          )}
        </QueryBuilderContainer>
      </StyledContentCard>
      {!disabled && (
        <FormStickyFooter
          Actions={
            <Button
              disabled={isSaveButtonDisabled}
              label='Save'
              loading={isLoading}
              onClick={() => saveQuery(ruleGroup)}
            />
          }
        />
      )}
    </>
  );
}
